import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Ticket, Plane, DollarSign, Calendar, Clock, CheckCircle, TrendingUp } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MisVentasPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [ventas, setVentas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [ventasRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/ventas`, { headers }),
        axios.get(`${API_URL}/ventas/stats`, { headers })
      ]);
      setVentas(ventasRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const pendingVentas = ventas.filter(v => v.status === 'pending');
  const completedVentas = ventas.filter(v => v.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="mis-ventas-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Mis Pasajeros
        </h1>
        <p className="text-slate-500 mt-1">Bienvenido, {user?.name}. Aquí está tu resumen de ventas.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm border-l-4 border-l-yellow-500">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Por Volar</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Completados</p>
              <p className="text-2xl font-bold text-green-600">{stats?.completed || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Plane className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Pasajes</p>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_tickets || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50 to-white">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Mi Total Vendido</p>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats?.total_sales || 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Flights - Priority */}
      <Card className="shadow-sm border-2 border-yellow-200" data-testid="pending-ventas-table">
        <CardHeader className="bg-yellow-50">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-500" />
            Pasajeros por Volar
            {pendingVentas.length > 0 && (
              <Badge className="bg-yellow-500 text-white ml-2">{pendingVentas.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {pendingVentas.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Plane className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No tienes pasajeros pendientes por volar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-yellow-50/50">
                  <TableHead>Pasajero</TableHead>
                  <TableHead>Ruta</TableHead>
                  <TableHead>Fecha Ida</TableHead>
                  <TableHead>Fecha Vuelta</TableHead>
                  <TableHead className="text-right">Pasajes</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingVentas.map((venta) => (
                  <TableRow key={venta.id} className="hover:bg-yellow-50/50">
                    <TableCell className="font-medium text-slate-900">
                      {venta.passenger_name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="font-medium">{venta.route_origin}</span>
                        <Plane className="w-4 h-4 text-blue-500 rotate-90" />
                        <span className="font-medium">{venta.route_destination}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-yellow-700">{venta.flight_date_departure}</span>
                    </TableCell>
                    <TableCell className="text-slate-600">{venta.flight_date_return || 'Solo ida'}</TableCell>
                    <TableCell className="text-right text-slate-600">{venta.tickets_count}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {formatCurrency(venta.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Completed Flights */}
      <Card className="shadow-sm" data-testid="completed-ventas-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Vuelos Completados
            {completedVentas.length > 0 && (
              <Badge className="bg-green-100 text-green-800 ml-2">{completedVentas.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {completedVentas.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No hay vuelos completados aún</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Pasajero</TableHead>
                  <TableHead>Ruta</TableHead>
                  <TableHead>Fecha Ida</TableHead>
                  <TableHead>Fecha Vuelta</TableHead>
                  <TableHead className="text-right">Pasajes</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedVentas.map((venta) => (
                  <TableRow key={venta.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">
                      {venta.passenger_name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span>{venta.route_origin}</span>
                        <Plane className="w-4 h-4 text-green-500 rotate-90" />
                        <span>{venta.route_destination}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{venta.flight_date_departure}</TableCell>
                    <TableCell className="text-slate-600">{venta.flight_date_return || 'Solo ida'}</TableCell>
                    <TableCell className="text-right text-slate-600">{venta.tickets_count}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {formatCurrency(venta.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MisVentasPage;
